import { Alert, Button, Card, Col, Form, Input, Row, Select, Space, Table, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ActivityRow = {
  id: string;
  code: string;
  name: string;
  session_id: string;
  capacity?: number | null;
  active_members?: number;
  is_active?: number | boolean;
};

type EligibleStudent = {
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  grade_code?: string;
  grade_name?: string;
};

type BulkForm = {
  session_id: string;
  activity_id: string;
  member_role: string;
  joined_on: string;
  grade_level_id?: string;
  q?: string;
  notes?: string;
};

type BulkResultRow = {
  student_id: string;
  ok: boolean;
  status?: string;
  error?: string;
};

function asArray<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: T[] }).data;
  }
  return [];
}

export default function ActivityMembershipBulkPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') || user?.permissions?.includes('education.admin')
  );
  const [form] = Form.useForm<BulkForm>();
  const sessionId = Form.useWatch('session_id', form);
  const activityId = Form.useWatch('activity_id', form);
  const gradeId = Form.useWatch('grade_level_id', form);
  const [searchQ, setSearchQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lastResults, setLastResults] = useState<BulkResultRow[] | null>(null);
  const presetActivity = searchParams.get('activity_id') || undefined;

  useEffect(() => {
    setSelectedIds([]);
    setLastResults(null);
  }, [sessionId, gradeId, activityId, appliedQ]);

  useEffect(() => {
    form.setFieldsValue({
      member_role: 'member',
      joined_on: new Date().toISOString().slice(0, 10),
    });
  }, [form]);

  const preset = useQuery({
    queryKey: ['education-activity', presetActivity],
    enabled: canManage && Boolean(presetActivity),
    queryFn: async () => {
      const res = await api.get<ActivityRow>(`/education/activities/${presetActivity}`);
      return res.data;
    },
  });

  useEffect(() => {
    const row = preset.data;
    if (!row) return;
    form.setFieldsValue({
      session_id: row.session_id,
      activity_id: row.id,
      member_role: 'member',
      joined_on: new Date().toISOString().slice(0, 10),
    });
  }, [form, preset.data]);

  const activities = useQuery({
    queryKey: ['education-activities', sessionId],
    enabled: canManage && Boolean(sessionId),
    queryFn: async () =>
      asArray<ActivityRow>(
        (await api.get('/education/activities', { params: { session_id: sessionId } })).data
      ),
  });

  const eligible = useQuery({
    queryKey: ['education-activity-eligible', sessionId, activityId, gradeId, appliedQ],
    enabled: Boolean(sessionId) && Boolean(activityId),
    queryFn: async () =>
      asArray<EligibleStudent>(
        (
          await api.get('/education/activity-memberships/eligible', {
            params: {
              session_id: sessionId,
              activity_id: activityId,
              grade_level_id: gradeId || undefined,
              q: appliedQ || undefined,
            },
          })
        ).data
      ),
  });

  const bulk = useMutation({
    mutationFn: (values: BulkForm & { student_ids: string[] }) =>
      api.post('/education/activity-memberships/bulk', {
        activity_id: values.activity_id,
        member_role: values.member_role,
        joined_on: values.joined_on,
        notes: values.notes || null,
        student_ids: values.student_ids,
      }),
    onSuccess: (result) => {
      const body = result.data as {
        succeeded?: number;
        failed?: number;
        waitlisted?: number;
        attempted?: number;
        results?: BulkResultRow[];
      };
      message.success(
        t('activityBulkEnrollmentDone', {
          succeeded: body?.succeeded ?? 0,
          failed: body?.failed ?? 0,
          waitlisted: body?.waitlisted ?? 0,
          attempted: body?.attempted ?? 0,
        })
      );
      setSelectedIds([]);
      setLastResults(Array.isArray(body?.results) ? body.results : null);
      queryClient.invalidateQueries({ queryKey: ['education-activity-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['education-activity-eligible'] });
      queryClient.invalidateQueries({ queryKey: ['education-activities'] });
      queryClient.invalidateQueries({ queryKey: ['education-activity'] });
      if (!(body?.failed && body.failed > 0)) {
        const actId = form.getFieldValue('activity_id');
        navigate(actId ? `/education/activities/${actId}/members` : '/education/activities/memberships');
      }
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const activeActivities = useMemo(
    () => (activities.data ?? []).filter((a) => !(a.is_active === 0 || a.is_active === false)),
    [activities.data]
  );
  const selectedActivity =
    activeActivities.find((a) => a.id === activityId) ||
    (preset.data?.id === activityId ? preset.data : undefined);
  const rows = eligible.data ?? [];
  const capacity = selectedActivity?.capacity;
  const members = Number(selectedActivity?.active_members ?? 0);
  const failedRows = (lastResults ?? []).filter((r) => !r.ok);

  if (!canManage) {
    return (
      <EducationPageChrome title={t('bulkActivityEnrollment')} description={t('bulkActivityEnrollmentIntro')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('bulkActivityEnrollment')}
      description={t('bulkActivityEnrollmentIntro')}
      extra={<Button onClick={() => navigate('/education/activities/memberships')}>{t('backToMemberships')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {activities.isError || preset.isError ? (
          <Alert type="error" showIcon message={t('activityLookupFailed')} />
        ) : null}

        <Card size="small">
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed) => {
              if ('session_id' in changed) {
                form.setFieldValue('activity_id', undefined);
                setAppliedQ('');
                setSearchQ('');
              }
            }}
            onFinish={(values) => {
              if (!selectedIds.length) {
                message.warning(t('activityBulkSelectStudents'));
                return;
              }
              bulk.mutate({ ...values, student_ids: selectedIds });
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
                  <EducationSessionSelect style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="activity_id" label={t('activity')} rules={[{ required: true }]}>
                  <Select
                    style={{ width: '100%' }}
                    loading={activities.isLoading || preset.isLoading}
                    options={activeActivities.map((a) => ({
                      value: a.id,
                      label: `${a.code} — ${a.name}${
                        a.capacity != null ? ` (${a.active_members ?? 0}/${a.capacity})` : ''
                      }`,
                    }))}
                    showSearch
                    optionFilterProp="label"
                    disabled={!sessionId}
                    notFoundContent={
                      !sessionId
                        ? t('activityBulkPickSession')
                        : activities.isFetching
                          ? t('libraryLoading')
                          : t('activityNoActivities')
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="grade_level_id" label={t('grade')}>
                  <EducationGradeSelect allowClear style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item label={t('search')}>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      value={searchQ}
                      allowClear
                      onChange={(e) => setSearchQ(e.target.value)}
                      onPressEnter={() => setAppliedQ(searchQ.trim())}
                    />
                    <Button onClick={() => setAppliedQ(searchQ.trim())}>{t('filter')}</Button>
                  </Space.Compact>
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="member_role" label={t('memberRole')} rules={[{ required: true }]}>
                  <Select
                    options={[
                      { value: 'member', label: t('roleMember') },
                      { value: 'captain', label: t('roleCaptain') },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Form.Item name="joined_on" label={t('joinedOn')} rules={[{ required: true }]}>
                  <Input placeholder="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label={t('notes')}>
              <Input.TextArea rows={2} style={{ maxWidth: 640 }} />
            </Form.Item>
            {activityId && capacity != null ? (
              <Alert
                type={members + selectedIds.length > Number(capacity) ? 'warning' : 'info'}
                showIcon
                style={{ marginBottom: 16 }}
                message={t('activityBulkCapacityHint', {
                  riders: members,
                  capacity: Number(capacity),
                  selected: selectedIds.length,
                })}
              />
            ) : null}
            <Space wrap>
              <Button
                type="primary"
                htmlType="submit"
                loading={bulk.isPending}
                disabled={!selectedIds.length || !activityId}
              >
                {t('enrollSelected', { count: selectedIds.length })}
              </Button>
              <Button onClick={() => setSelectedIds(rows.map((r) => r.student_id))} disabled={!rows.length}>
                {t('selectAllVisible')}
              </Button>
              <Button onClick={() => setSelectedIds([])} disabled={!selectedIds.length}>
                {t('clearSelection')}
              </Button>
            </Space>
          </Form>
        </Card>

        {failedRows.length ? (
          <Card size="small" title={t('activityBulkFailures')}>
            <Typography.Paragraph type="secondary">{t('activityBulkFailuresHint')}</Typography.Paragraph>
            <Table
              rowKey="student_id"
              size="small"
              pagination={false}
              dataSource={failedRows}
              columns={[
                { title: t('student'), dataIndex: 'student_id', width: 280 },
                { title: t('error'), dataIndex: 'error' },
              ]}
            />
            <Button
              style={{ marginTop: 12 }}
              onClick={() => {
                const actId = form.getFieldValue('activity_id');
                navigate(actId ? `/education/activities/${actId}/members` : '/education/activities/memberships');
              }}
            >
              {t('viewMembers')}
            </Button>
          </Card>
        ) : null}

        <Card size="small" title={t('eligibleStudents')}>
          {!sessionId ? (
            <Alert type="info" showIcon message={t('activityBulkPickSession')} />
          ) : !activityId ? (
            <Alert type="info" showIcon message={t('activityBulkPickActivity')} />
          ) : eligible.isError ? (
            <Alert type="error" showIcon message={t('activityEligibleFailed')} />
          ) : (
            <Table
              rowKey="student_id"
              size="small"
              loading={eligible.isLoading || eligible.isFetching}
              dataSource={rows}
              pagination={{ pageSize: 50 }}
              locale={{ emptyText: t('activityEligibleEmpty') }}
              rowSelection={{
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys.map(String)),
                preserveSelectedRowKeys: true,
              }}
              columns={[
                {
                  title: t('student'),
                  render: (_: unknown, row: EligibleStudent) =>
                    `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
                },
                {
                  title: t('grade'),
                  render: (_: unknown, row: EligibleStudent) =>
                    row.grade_name || row.grade_code || t('allGrades'),
                },
              ]}
            />
          )}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
